import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LogOut, Trash2, Inbox, Mail, Newspaper, BarChart3, Plus } from "lucide-react";
import { API } from "@/constants/assets";

const STATUSES = ["nouveau", "en cours", "devis envoyé", "terminé"];
const inputCls = "field w-full px-4 py-3 text-sm";

export default function Admin() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [tab, setTab] = useState("requests");
  const [requests, setRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [postForm, setPostForm] = useState({ title: "", category: "Patrimoine", excerpt: "", image: "" });

  const loadAll = useCallback(async () => {
    try {
      const [r, m, p, s] = await Promise.all([
        axios.get(`${API}/requests`, { withCredentials: true }),
        axios.get(`${API}/messages`, { withCredentials: true }),
        axios.get(`${API}/posts`),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
      ]);
      setRequests(r.data); setMessages(m.data); setPosts(p.data); setStats(s.data);
    } catch { /* session expired */ }
  }, []);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => { setUser(r.data); })
      .catch(() => setUser(false));
  }, []);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
      setUser(data);
      toast.success("Bienvenue à l'atelier");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erreur de connexion");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
  };

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/requests/${id}`, { status }, { withCredentials: true });
    setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    toast.success("Statut mis à jour");
  };

  const deleteRequest = async (id) => {
    await axios.delete(`${API}/requests/${id}`, { withCredentials: true });
    setRequests((rs) => rs.filter((r) => r.id !== id));
  };

  const deleteMessage = async (id) => {
    await axios.delete(`${API}/messages/${id}`, { withCredentials: true });
    setMessages((ms) => ms.filter((m) => m.id !== id));
  };

  const createPost = async (e) => {
    e.preventDefault();
    const { data } = await axios.post(`${API}/posts`, postForm, { withCredentials: true });
    setPosts((ps) => [data, ...ps]);
    setPostForm({ title: "", category: "Patrimoine", excerpt: "", image: "" });
    toast.success("Article publié");
  };

  const deletePost = async (id) => {
    await axios.delete(`${API}/posts/${id}`, { withCredentials: true });
    setPosts((ps) => ps.filter((p) => p.id !== id));
  };

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center font-serif-d italic text-[#7A7367]">Chargement…</div>;
  }

  if (user === false) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-md border hairline bg-[#14120F] p-10">
          <div className="font-serif-d text-2xl tracking-[0.18em] text-center">ARCHWOOD <span className="gold-text">FRANCE</span></div>
          <p className="text-center text-[10px] font-mono-d tracking-[0.35em] uppercase text-[#7A7367] mt-2">Espace Atelier</p>
          <form onSubmit={login} className="mt-10 space-y-4">
            <input data-testid="admin-login-email" type="email" required placeholder="Email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className={inputCls} />
            <input data-testid="admin-login-password" type="password" required placeholder="Mot de passe" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className={inputCls} />
            <button data-testid="admin-login-button" type="submit" className="gold-btn w-full border border-[#D4AF37] py-3.5 text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]">
              Connexion
            </button>
          </form>
          <button
            data-testid="admin-demo-fill-button"
            onClick={() => setLoginForm({ email: "admin@archwood.fr", password: "Archwood2026!" })}
            className="mt-4 w-full text-[10px] font-mono-d tracking-[0.2em] uppercase text-[#7A7367] hover:text-[#C5A059] transition-colors"
          >
            Pré-remplir (démo)
          </button>
          <Link to="/" className="block mt-6 text-center text-xs text-[#7A7367] hover:text-[#F7F4EE] transition-colors">← Retour au site</Link>
        </div>
      </div>
    );
  }

  const tabs = [
    ["requests", <Inbox key="i" size={14} />, "Demandes", requests.length],
    ["messages", <Mail key="m" size={14} />, "Messages", messages.length],
    ["journal", <Newspaper key="n" size={14} />, "Journal", posts.length],
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen">
      <header className="border-b hairline bg-[#14120F]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="font-serif-d tracking-[0.18em]">ARCHWOOD <span className="gold-text">ATELIER</span></div>
          <div className="flex items-center gap-5">
            <Link to="/" className="text-xs text-[#7A7367] hover:text-[#F7F4EE] transition-colors">Voir le site</Link>
            <span className="text-xs text-[#B8B0A2]">{user.email}</span>
            <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 text-xs text-[#D4AF37] hover:text-[#F7F4EE] transition-colors">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              ["Demandes", stats.requests_total],
              ["Nouvelles", stats.requests_new],
              ["Messages", stats.messages],
              ["Articles", stats.posts],
            ].map(([label, v]) => (
              <div key={label} data-testid={`admin-stat-${label.toLowerCase().replace(/\s/g, "-")}`} className="border hairline bg-[#14120F] p-6">
                <BarChart3 size={14} className="text-[#C5A059]" />
                <div className="font-serif-d text-4xl mt-3 gold-text">{v}</div>
                <div className="text-[10px] font-mono-d tracking-[0.25em] uppercase text-[#7A7367] mt-1">{label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mb-8 flex-wrap">
          {tabs.map(([key, icon, label, count]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-5 py-2.5 border text-[11px] tracking-[0.2em] uppercase transition-colors duration-300 ${tab === key ? "border-[#D4AF37] text-[#D4AF37]" : "hairline border text-[#7A7367] hover:text-[#F7F4EE]"}`}
            >
              {icon} {label} <span className="opacity-60">({count})</span>
            </button>
          ))}
        </div>

        {tab === "requests" && (
          <div className="space-y-4" data-testid="admin-requests-list">
            {requests.length === 0 && <p className="font-serif-d italic text-[#7A7367]">Aucune demande pour le moment.</p>}
            {requests.map((r) => (
              <div key={r.id} data-testid="admin-quote-row-item" className="border hairline bg-[#14120F] p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono-d text-xs gold-text tracking-widest">{r.reference}</span>
                      <span className="text-[10px] font-mono-d text-[#7A7367]">{new Date(r.created_at).toLocaleString("fr-FR")}</span>
                    </div>
                    <h3 className="font-serif-d text-2xl mt-2">{r.product_type} × {r.quantity}</h3>
                    <p className="text-sm text-[#B8B0A2] mt-1">
                      {[r.wood, r.glazing, r.profile, r.shape, r.height && r.width ? `${r.height}×${r.width} mm` : ""].filter(Boolean).join(" · ")}
                    </p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {r.sculpture && <span className="text-[10px] font-mono-d uppercase tracking-widest border border-[#D4AF37]/50 text-[#D4AF37] px-2 py-0.5">Maître sculpteur</span>}
                      {r.copie_ancien && <span className="text-[10px] font-mono-d uppercase tracking-widest border border-[#D4AF37]/50 text-[#D4AF37] px-2 py-0.5">Copie d'ancien</span>}
                    </div>
                    {(r.details || r.message) && <p className="text-sm text-[#7A7367] mt-3 max-w-2xl">{r.details} {r.message}</p>}
                    <p className="text-sm mt-3 text-[#F7F4EE]">{r.name} — <a className="text-[#C5A059]" href={`mailto:${r.email}`}>{r.email}</a> {r.phone && `· ${r.phone}`} {r.project_address && `· ${r.project_address}`}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      data-testid={`admin-status-select-${r.reference}`}
                      value={r.status}
                      onChange={(e) => setStatus(r.id, e.target.value)}
                      className="field px-3 py-2 text-xs uppercase tracking-widest"
                    >
                      {STATUSES.map((s) => <option key={s} value={s} className="bg-[#14120F]">{s}</option>)}
                    </select>
                    <button data-testid={`admin-delete-request-${r.reference}`} onClick={() => deleteRequest(r.id)} className="p-2 text-[#7A7367] hover:text-red-400 transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-4" data-testid="admin-messages-list">
            {messages.length === 0 && <p className="font-serif-d italic text-[#7A7367]">Aucun message.</p>}
            {messages.map((m) => (
              <div key={m.id} className="border hairline bg-[#14120F] p-6 flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm text-[#F7F4EE]">{m.name} — <a className="text-[#C5A059]" href={`mailto:${m.email}`}>{m.email}</a> {m.phone && `· ${m.phone}`}</p>
                  <p className="text-sm text-[#B8B0A2] mt-2">{m.message}</p>
                  <span className="text-[10px] font-mono-d text-[#7A7367]">{new Date(m.created_at).toLocaleString("fr-FR")}</span>
                </div>
                <button onClick={() => deleteMessage(m.id)} className="p-2 text-[#7A7367] hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        )}

        {tab === "journal" && (
          <div data-testid="admin-journal-panel">
            <form onSubmit={createPost} className="border hairline bg-[#14120F] p-6 md:p-8 grid md:grid-cols-2 gap-4 mb-8">
              <input data-testid="admin-post-title" required placeholder="Titre de l'article" value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} className={inputCls} />
              <input data-testid="admin-post-category" placeholder="Catégorie" value={postForm.category} onChange={(e) => setPostForm({ ...postForm, category: e.target.value })} className={inputCls} />
              <input data-testid="admin-post-image" placeholder="URL de l'image (optionnel)" value={postForm.image} onChange={(e) => setPostForm({ ...postForm, image: e.target.value })} className={`${inputCls} md:col-span-2`} />
              <textarea data-testid="admin-post-excerpt" rows={3} placeholder="Extrait" value={postForm.excerpt} onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })} className={`${inputCls} md:col-span-2 resize-none`} />
              <button data-testid="admin-post-submit" type="submit" className="gold-btn border border-[#D4AF37] py-3 text-[11px] tracking-[0.25em] uppercase text-[#D4AF37] md:col-span-2 inline-flex items-center justify-center gap-2">
                <Plus size={14} /> Publier l'article
              </button>
            </form>
            <div className="space-y-3">
              {posts.map((p) => (
                <div key={p.id} className="border hairline bg-[#14120F] px-6 py-4 flex items-center justify-between gap-4">
                  <div>
                    <span className="text-[10px] font-mono-d uppercase tracking-widest text-[#D4AF37]">{p.category}</span>
                    <h4 className="font-serif-d text-xl">{p.title}</h4>
                  </div>
                  <button onClick={() => deletePost(p.id)} className="p-2 text-[#7A7367] hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
